import type { ApiProblem, Catalogue, SessionResponse, ShopResponse } from './types';

// Base URL is stamped in at build time (.env.uat / .env.production); in `vite dev`
// it is empty and the dev server proxies /s and /public to the UAT API.
const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
export const SESSION_HEADER = 'X-Shop-Session';

export class ShopError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function problem(res: Response): Promise<ShopError> {
  let body: ApiProblem = {};
  try {
    body = (await res.json()) as ApiProblem;
  } catch {
    // Not JSON (a proxy page, say); the status is all we know.
  }
  return new ShopError(res.status, body.code ?? `http_${res.status}`, body.title ?? body.message ?? 'Something went wrong.');
}

function headers(session: string | null, extra?: Record<string, string>): Headers {
  const h = new Headers(extra);
  if (session) h.set(SESSION_HEADER, session);
  return h;
}

export async function getShop(slug: string, session: string | null): Promise<ShopResponse> {
  const res = await fetch(`${BASE}/s/${encodeURIComponent(slug)}/shop`, { headers: headers(session), cache: 'no-store' });
  if (!res.ok) throw await problem(res);
  return (await res.json()) as ShopResponse;
}

/** Exchanges a contact token for a session, or refreshes/creates an anonymous one. */
export async function openSession(slug: string, session: string | null, contactToken: string | null): Promise<SessionResponse> {
  const res = await fetch(`${BASE}/s/${encodeURIComponent(slug)}/session`, {
    method: 'POST',
    headers: headers(session, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ c: contactToken }),
  });
  if (!res.ok) throw await problem(res);
  return (await res.json()) as SessionResponse;
}

export interface CatalogueResult {
  catalogue: Catalogue;
  etag: string | null;
  fromCache: boolean;
}

/**
 * The catalogue for the session's group. `cached` is what the page last stored
 * for this (slug, group); its ETag goes up as If-None-Match and a 304 means the
 * stored document is still the current one — the common case on a revisit.
 */
export async function getCatalogue(
  slug: string,
  session: string | null,
  groupId: number,
  version: number,
  cached: { etag: string; catalogue: Catalogue } | null,
): Promise<CatalogueResult> {
  const h = headers(session);
  if (cached) h.set('If-None-Match', cached.etag);
  const url = `${BASE}/s/${encodeURIComponent(slug)}/catalogue?v=${version}&g=${groupId}`;
  const res = await fetch(url, { headers: h });
  if (res.status === 304 && cached) return { catalogue: cached.catalogue, etag: cached.etag, fromCache: true };
  if (!res.ok) throw await problem(res);
  return { catalogue: (await res.json()) as Catalogue, etag: res.headers.get('ETag'), fromCache: false };
}
